// ◉ xlVISION · Powering Business Together!
// © ExcelVision, Lda. — Braga, Portugal
// ( ·:·●●·:· ) xlVISION | Powering Business Together!
//
// ============================================================================
// A SAÍDA DO TRAVÃO — uma só, para todos os ecrãs que escrevem no plano
// ============================================================================
//
// 📗 Cliente, 2026-08-18: *«não tem onde pôr o motivo também… neste caso. Tens de atualizar para
//    confirmar em TODOS OS MENUS onde apareça esta notificação»*.
//
// ⚠️⚠️ **E ele apanhou-me duas vezes com o mesmo defeito, em ecrãs diferentes.** Da primeira
//    corrigi o `EditarAlocacao`; da segunda foi o rearranjo. 📌 **A terceira não pode depender de
//    eu me lembrar** — daí este módulo e a guarda estrutural que o acompanha.
//
// ---------------------------------------------------------------------------
// O QUE O SERVIDOR RESPONDE, e são TRÊS coisas e não duas
// ---------------------------------------------------------------------------
//
//   | `erro` | o que quer dizer | saída |
//   |---|---|---|
//   | `travado` | confirmado, ou dentro do horizonte | **há**: aceitação explícita + motivo |
//   | `travado_por_alfinete` | alguém marcou «não mexer aqui» | tirar o alfinete — outro gesto |
//   | `travado_definitivamente` | já passou, ou está fechado | **não há** |
//
//   ⚠️ Oferecer «avançar» nos três punha uma saída à frente do impossível — e um botão que não
//   pode funcionar é pior do que nenhum, porque ensina a desconfiar dos que funcionam.
//
// ⚠️⚠️ **`forcavel` VEM DENTRO DE `contexto`, e não no topo.** À primeira li `c.forcavel` — que
//    nunca existiu na resposta —, logo o botão era código morto: a condição era sempre falsa e
//    nada no ecrã o dizia.
// ============================================================================

import { useState } from 'react';

export interface Recusa {
  /** `true` quando o servidor recusou e quem chama deve parar. */
  readonly recusou: boolean;
  /** A mensagem a mostrar. Já traz a explicação do servidor quando ela existe. */
  readonly mensagem: string;
  /** ⚠️ `true` só quando o SERVIDOR diz que é forçável. O ecrã repete, não decide. */
  readonly forcavel: boolean;
}

/** A forma da recusa, tal como o servidor a escreve. */
export interface CorpoDeRecusa {
  readonly detalhe?: string | undefined;
  readonly erro?: string | undefined;
  readonly contexto?: { readonly forcavel?: boolean | undefined } | undefined;
}

/**
 * A REGRA, num sítio só: este corpo é uma recusa, e tem saída?
 *
 * ⚠️⚠️ **Recebe o CORPO e não a `Response`, e é deliberado.** Metade dos ecrãs já lê o corpo para
 * os seus próprios campos (`referenciaNova`, `colisoes`, `pedacos`…), e um `resp.json()` chamado
 * duas vezes rebenta com «body already read» — com o erro do leitor a aparecer no lugar do erro
 * do servidor. 📌 Assim quem já leu passa o que tem, e a regra continua a viver **aqui**.
 *
 * ⚠️ Escrevi primeiro a condição à mão dentro de um ecrã, que é exactamente o que este módulo
 * existe para impedir. Uma segunda cópia é uma segunda verdade.
 */
/**
 * ---- ⚠️⚠️ O CAMPO DO MOTIVO ESTAVA ESCRITO EM DOIS SÍTIOS, PALAVRA POR PALAVRA (2026-09-02) --
 *
 * 📗 Cliente: ***«tens de atualizar para confirmar em TODOS OS MENUS onde apareça esta
 * notificação»*** — a frase que fez nascer este módulo.
 *
 * ⚠️⚠️ **E o próprio cabeçalho deste ficheiro escreve a regra que foi quebrada:** *«uma segunda
 * cópia é uma segunda verdade»*, e *«a terceira não pode depender de eu me lembrar»*. Havia duas:
 * o `SaidaDoTravao`, aqui em baixo, e o `EditarAlocacao.tsx`, que desenha o **mesmo** `<label
 * className="largar-motivo">` com o **mesmo** rótulo e o **mesmo** exemplo, à mão.
 *
 * 📌 **E já existe uma TERCEIRA formulação, no `LargarPedido.tsx`** — *«fica na auditoria e
 * pendente de ratificação (P-22)»*. ⚠️ **Essa NÃO se unifica, e a diferença é do domínio, não do
 * texto:** ali o motivo é o de uma **divergência legal** (D-007) e vai mesmo para a fila da P-22;
 * aqui é o de **forçar um travão**, e o que fica registado é *o que isto era antes*. Juntá-las
 * numa constante fazia um dos dois ecrãs prometer uma ratificação que não acontece — que é pior
 * do que a duplicação, porque a frase deixava de ser verdade.
 *
 * ⚠️ O rótulo é só o **sufixo**: o «Motivo <strong>obrigatório</strong>» tem marcação a meio, e
 * uma constante com HTML lá dentro obrigava a `dangerouslySetInnerHTML` num sítio onde o texto é
 * quase todo do domínio. Ver a guarda em `motivo-num-texto-so.test.ts`.
 */
export const MOTIVO_SUFIXO = '— fica na auditoria com o que isto era antes';

/** O exemplo do campo — mesma razão, mesma guarda. */
export const MOTIVO_EXEMPLO = 'p. ex. «o cliente pediu para trocar o autocarro»';

/**
 * ---- ⚠️⚠️ AS RECUSAS QUE PEDEM «MOTIVO E REENVIE» SEM SEREM UM `travado` (2026-09-29) ------
 *
 * 📗 Cliente: *«confirmados pede motivo, mas não existe caixa para indicar o motivo»*.
 *
 * ⚠️⚠️ **A regra só reconhecia UMA das formas em que o servidor diz «escreva o motivo e repita»**
 * — o `travado` forçável do `travoes.ts`. Há mais três, e todas escrevem essa instrução por
 * extenso na frase que o ecrã mostrava por cima de… nada:
 *
 *   · `motivo_obrigatorio` (400) — o travão aceite sem motivo, a espera excessiva que uma
 *     rotação impõe (`registarDivergenciasDaEspera`, que o «trazer de volta» passou a correr), e
 *     a efectivação com viagens em espera;
 *   · `mudanca_em_definitivo` (409) — trocar a viatura de um registo já efectivado
 *     (`mover.ts`): *«repita o pedido com `aceitaTravoes: true` e um motivo»*;
 *   · `viagem_em_espera` (409) — confirmar um pedido com uma viagem sem autocarro
 *     (`efectivar.ts`). O `PorConfirmar` já a acrescentava à mão, e era a segunda leitura desta
 *     regra fora daqui — *«uma segunda cópia é uma segunda verdade»*.
 *
 * 📌 **O que continua SEM saída não muda:** `travado_definitivamente` (o passado e o fechado) e
 * `travado_por_alfinete` (tirar o alfinete é outro gesto). Reenviar com aceitação e motivo é o
 * que o servidor pede nas quatro de cima — e só nessas.
 */
export const PEDEM_MOTIVO_E_REENVIO: readonly string[] = [
  'motivo_obrigatorio',
  'mudanca_em_definitivo',
  'viagem_em_espera',
];

export function recusaDoCorpo(
  corpo: CorpoDeRecusa | null,
  estado: number,
  oQue: string,
): Recusa {
  return {
    recusou: true,
    mensagem: corpo?.detalhe ?? corpo?.erro ?? `${oQue} respondeu ${estado}`,
    forcavel:
      (corpo?.erro === 'travado' && corpo.contexto?.forcavel === true) ||
      PEDEM_MOTIVO_E_REENVIO.includes(corpo?.erro ?? ''),
  };
}

/**
 * O mesmo, para quem ainda não leu o corpo.
 *
 * ⚠️ **O corpo lê-se UMA vez** — ver a nota de `recusaDoCorpo`.
 */
export async function lerRecusa(resp: Response, oQue: string): Promise<Recusa> {
  const c = (await resp.json().catch(() => null)) as CorpoDeRecusa | null;
  if (resp.ok) return { recusou: false, mensagem: '', forcavel: false };
  return recusaDoCorpo(c, resp.status, oQue);
}

/**
 * O estado da saída do travão — o motivo e se ela está aberta.
 *
 * ⚠️ Um `useState` por ecrã dava a mesma lógica escrita sete vezes, e a sétima nasceria sem ela.
 */
export function useTravao(): {
  readonly forcavel: boolean;
  readonly motivo: string;
  readonly definirMotivo: (v: string) => void;
  /** Chamar com a `Recusa`: abre a saída quando o servidor disser que há. */
  readonly registar: (r: Recusa) => void;
  readonly limpar: () => void;
} {
  const [forcavel, definirForcavel] = useState(false);
  const [motivo, definirMotivo] = useState('');
  return {
    forcavel,
    motivo,
    definirMotivo,
    registar: (r: Recusa) => {
      if (r.forcavel) definirForcavel(true);
    },
    limpar: () => {
      definirForcavel(false);
      definirMotivo('');
    },
  };
}

/**
 * O campo do motivo e o botão de avançar — **o sítio que faltava**.
 *
 * 📗 *«aparece para indicar o motivo mas ONDE???»*
 *
 * ⚠️⚠️ **Só aparece depois de o servidor recusar**, e nunca antes: mostrá-lo sempre transformava
 * um gesto de um clique num formulário. E o botão **exige motivo** — é o que protege a empresa
 * (art. 10.º n.º 3): não é o sistema ter impedido, é haver prova de que avisou e de que uma
 * pessoa decidiu com razão escrita.
 */
export function SaidaDoTravao({
  travao,
  aGravar,
  avancar,
  oQue,
  motivoProprio,
}: {
  readonly travao: ReturnType<typeof useTravao>;
  readonly aGravar: boolean;
  /** Repete a escrita com aceitação explícita e o motivo. */
  readonly avancar: () => void;
  /** O gesto, para o botão o nomear: «avançar mesmo assim» sobre o quê. */
  readonly oQue: string;
  /**
   * ⚠️⚠️ **O motivo que o ecrã JÁ recolhe, quando o gesto o exige sempre.**
   *
   * 📌 «Partir» e «cancelar» pedem motivo por si — são gestos que mudam um serviço já comunicado.
   * Nesses, mostrar um **segundo** campo de motivo obrigava a escolher qual preencher, e o que
   * ficasse vazio bloqueava o botão sem dizer porquê. Aqui só falta a **aceitação explícita**.
   */
  readonly motivoProprio?: string | undefined;
}) {
  if (!travao.forcavel) return null;
  const motivo = motivoProprio ?? travao.motivo;
  return (
    <div className="travao-saida">
      {motivoProprio !== undefined ? null : (
      <label className="largar-motivo">
        <span>
          Motivo <strong>obrigatório</strong> {MOTIVO_SUFIXO}
        </span>
        <textarea
          value={travao.motivo}
          onChange={(e) => travao.definirMotivo(e.target.value)}
          rows={2}
          autoFocus
          placeholder={MOTIVO_EXEMPLO}
        />
      </label>
      )}
      <div className="motivo-accoes faixa-quebra">
        <button
          type="button"
          className="btn btn--principal"
          disabled={aGravar || motivo.trim() === ''}
          onClick={avancar}
          title="avançar apesar do travão — fica registado na auditoria com o motivo"
        >
          Avançar mesmo assim — {oQue}
        </button>
        {motivo.trim() === '' ? (
          <span className="contas-sub">escreva o motivo antes de avançar</span>
        ) : null}
      </div>
    </div>
  );
}
